import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../config/api';

const AuthContext = createContext(null);

const TOKEN_KEY = 'petsbox_session_token';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const API = (() => {
    const envUrl = process.env.REACT_APP_BACKEND_URL;
    if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
      return `${window.location.origin}/api`;
    }
    return `${envUrl || ''}/api`;
  })();

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
        try { localStorage.removeItem(TOKEN_KEY); } catch {}
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const loginWithEmail = async (email, password) => {
    try {
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      if (response.ok) {
        const userData = await response.json();
        if (userData.session_token) {
          try { localStorage.setItem(TOKEN_KEY, userData.session_token); } catch {}
        }
        setUser(userData);
        return userData;
      } else {
        let errMsg = 'Login failed';
        try {
          const err = await response.json();
          errMsg = err?.detail || errMsg;
        } catch {}
        throw new Error(errMsg);
      }
    } catch (err) {
      if (err.message) throw err;
      throw new Error('Connection error');
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    try { localStorage.removeItem(TOKEN_KEY); } catch {}
    setUser(null);
    window.location.href = '/';
  };

  const value = {
    user,
    loading,
    login,
    loginWithEmail,
    logout,
    setUser,
    checkAuth,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
